import React, { useEffect, useState } from "react";
import { CheckCircle2, AlertTriangle, RefreshCcw } from "lucide-react";
import { apiGet } from "@/lib/api";
import { cn } from "@/lib/utils";

/**
 * Slim banner that surfaces the freshness of price/score data.
 * - Green when prices are <24h old.
 * - Amber when stale.
 */
const DataFreshnessBanner = ({ compact = false, className = "" }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const d = await apiGet("/data-freshness");
      setData(d);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const i = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(i);
  }, []);

  if (loading || !data) return null;
  // Phase 11 (Fix 6) — Use banner.level (ok|warning|danger) for proper
  // 3-state colouring instead of just stale boolean.
  const level = data.banner?.level || (data.stale ? "warning" : "ok");
  const Icon =
    level === "danger" ? AlertTriangle : level === "warning" ? AlertTriangle : CheckCircle2;

  return (
    <div
      data-testid="data-freshness-banner"
      data-level={level}
      className={cn(
        "flex items-center gap-2 text-xs rounded-md border px-3 py-1.5",
        level === "danger"
          ? "border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))]"
          : level === "warning"
            ? "border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))]"
            : "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
        className
      )}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span className="flex-1 truncate" data-testid="data-freshness-message">
        {data.banner?.message}
      </span>
      {!compact && data.scoring?.last_recompute_at_local && (
        <span className="hidden sm:inline text-[11px] opacity-70">
          · Scores: {data.scoring.last_recompute_at_local.replace(" (Sri Lanka time)", "")}
        </span>
      )}
      <button
        onClick={load}
        className="opacity-60 hover:opacity-100 transition-opacity"
        aria-label="Refresh freshness check"
        data-testid="data-freshness-refresh"
      >
        <RefreshCcw className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

export default DataFreshnessBanner;
